import React, { useState, useRef, useEffect } from 'react';
import './style.css';

const AutocompleteSelect = ({
  value,
  onChange,
  options,
  placeholder = "Оберіть значення",
  loading = false,
  loadingMore = false,
  hasMore = false,
  onSearch,
  onLoadMore,
  error,
  label,
  disabled = false,
  className = '',
  fieldsValue = ['name'],
  debounceDelay = 500
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [hasSelected, setHasSelected] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  const selectedOption = options.find(opt => opt._id === value);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const updateDropdownWidth = () => {
      if (wrapperRef.current) setDropdownWidth(wrapperRef.current.offsetWidth);
    };
    updateDropdownWidth();
    window.addEventListener('resize', updateDropdownWidth);
    return () => window.removeEventListener('resize', updateDropdownWidth);
  }, []);

  // Реагируем ТОЛЬКО на изменение value, не на options
  useEffect(() => {
    if (value) {
      const opt = options.find(o => o._id === value);
      if (opt) {
        setInputValue(fieldsValue.map(f => opt[f]).join(" "));
        setHasSelected(true);
      }
      // value есть, но опция не найдена в текущем списке — не трогаем inputValue
    } else {
      // value явно сброшен — очищаем
      setInputValue('');
      setHasSelected(false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        if (value && selectedOption) {
          const valVisible = fieldsValue.map(f => selectedOption[f]).join(" ");
          if (inputValue !== valVisible) setInputValue(valVisible);
        } else if (!value) {
          setInputValue('');
          setHasSelected(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, selectedOption, inputValue]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) onLoadMore();
      },
      { root: listRef.current, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loadingMore, isOpen, filteredOptions]);

  const prevInputValue = useRef('');
useEffect(() => {
  if (prevInputValue.current !== inputValue) {
    console.trace('inputValue changed:', prevInputValue.current, '->', inputValue);
    prevInputValue.current = inputValue;
  }
});

  const performSearch = (searchValue) => {
    if (onSearch) {
      onSearch(searchValue);
    } else if (searchValue) {
      const filtered = options.filter(option => {
        const searchString = fieldsValue.map(f => option[f] || '').join(' ').toLowerCase();
        return searchString.includes(searchValue.toLowerCase());
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (newValue.length === 0) {
      onChange('');
      setHasSelected(false);
      performSearch('');
      setIsOpen(false);
    } else {
      setIsOpen(true);
      debounceTimerRef.current = setTimeout(() => {
        performSearch(newValue);
      }, debounceDelay);
    }
  };

  const handleSelect = (option) => {
    onChange(option._id);
    const displayValue = fieldsValue.map(f => option[f]).join(' ');
    setInputValue(displayValue);
    setHasSelected(true);
    setIsOpen(false);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      if (!onSearch && inputValue.length === 0) setFilteredOptions(options);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      if (value && selectedOption) {
        setInputValue(fieldsValue.map(f => selectedOption[f]).join(' '));
      } else if (!value) {
        setInputValue('');
      }
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    } else if (e.key === 'Enter' && isOpen && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelect(filteredOptions[0]);
    } else if (e.key === 'Backspace' && hasSelected && inputValue === fieldsValue.map(f => selectedOption?.[f]).join(' ')) {
      e.preventDefault();
      onChange('');
      setInputValue('');
      setHasSelected(false);
      setIsOpen(true);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      performSearch('');
    } else if (e.key === 'ArrowDown' && !isOpen) {
      setIsOpen(true);
    }
  };

  const clearSelection = () => {
    onChange('');
    setInputValue('');
    setHasSelected(false);
    setIsOpen(true);
    inputRef.current?.focus();
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    performSearch('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (newIsOpen) {
        inputRef.current?.focus();
        if (!onSearch && inputValue.length === 0) setFilteredOptions(options);
      }
    }
  };

  const getDisplayValue = (option) => fieldsValue.map(f => option[f]).join(' ');

  return (
    <div className={`form-group autocomplete-container ${className}`} ref={wrapperRef}>
      {label && <label className="form-label">{label}</label>}

      <div className="autocomplete-wrapper">
        <div className="autocomplete-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className={`form-control ${error ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
            value={inputValue.length > 70 ? inputValue.slice(0, 70) + "..." : inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={!onSearch && options.length === 0}
          />

          <div className="autocomplete-buttons">
            {hasSelected && (
              <button
                type="button"
                className="autocomplete-clear-btn"
                onClick={clearSelection}
                disabled={disabled}
                aria-label="Очистити вибір"
              >
                ×
              </button>
            )}
            <button
              type="button"
              className={`autocomplete-toggle-btn ${isOpen ? 'open' : ''}`}
              onClick={toggleDropdown}
              disabled={disabled}
              aria-label={isOpen ? "Згорнути список" : "Розгорнути список"}
            >
              ▼
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="autocomplete-dropdown" style={{ width: dropdownWidth }}>
            {loading ? (
              <div className="autocomplete-loading">
                <div className="spinner"></div>
                Завантаження...
              </div>
            ) : filteredOptions.length > 0 ? (
              <div className="autocomplete-list" ref={listRef}>
                {filteredOptions.map(option => (
                  <div
                    key={option._id}
                    className={`autocomplete-item ${value === option._id ? 'selected' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    {getDisplayValue(option)}
                    {option.description && (
                      <div className="autocomplete-item-description">
                        {option.description}
                      </div>
                    )}
                  </div>
                ))}

                {hasMore && (
                  <div ref={sentinelRef} className="autocomplete-sentinel">
                    {loadingMore && (
                      <div className="autocomplete-loading-more">
                        <div className="spinner spinner-sm"></div>
                        Завантаження...
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="autocomplete-no-results">
                {inputValue.length > 0 ? 'Нічого не знайдено' : 'Немає доступних опцій'}
              </div>
            )}
          </div>
        )}

        {error && <div className="invalid-feedback">{error}</div>}
      </div>
    </div>
  );
};

export default AutocompleteSelect;